import React, { useEffect, useMemo, useState } from 'react';
import { GeoJSON, Pane } from 'react-leaflet';
import { getGeometryLayerRequestPath } from '../../config/geometry-layer-urls';
import { fillOpacityForProtectionZone, kindForProtectionZone, protectionZoneColors, protectionZoneDrawOrder, zoneLabel, } from '../../config/conservation-colors';
import { useDisplayPreferences } from '../../displayPreferences-context';
import { apiGet } from '../../apiHelpers';
import './protection-zones-layer.css';
const KIND_ORDER = [
    'consultationNational',
    'secondaryNational',
    'primaryNational',
    'worldHeritage',
];
function sortProtectionZones(data) {
    const features = [...(data.features || [])].sort((a, b) => {
        var _a, _b, _c, _d, _e, _f;
        const aKind = kindForProtectionZone(zoneLabel(a.properties));
        const bKind = kindForProtectionZone(zoneLabel(b.properties));
        const aOrder = protectionZoneDrawOrder[aKind];
        const bOrder = protectionZoneDrawOrder[bKind];
        if (aOrder !== bOrder)
            return aOrder - bOrder;
        const aId = Number((_c = (_b = (_a = a.properties) === null || _a === void 0 ? void 0 : _a.OBJECTID) !== null && _b !== void 0 ? _b : a.id) !== null && _c !== void 0 ? _c : 0);
        const bId = Number((_f = (_e = (_d = b.properties) === null || _d === void 0 ? void 0 : _d.OBJECTID) !== null && _e !== void 0 ? _e : b.id) !== null && _f !== void 0 ? _f : 0);
        return aId - bId;
    });
    return { type: 'FeatureCollection', features };
}
function partitionByKind(data) {
    const buckets = {
        worldHeritage: [],
        primaryNational: [],
        secondaryNational: [],
        consultationNational: [],
    };
    (data.features || []).forEach((ft) => {
        if (ft.type !== 'Feature')
            return;
        const kind = kindForProtectionZone(zoneLabel(ft.properties));
        buckets[kind].push(ft);
    });
    const out = {};
    KIND_ORDER.forEach((k) => {
        out[k] = { type: 'FeatureCollection', features: buckets[k] };
    });
    return out;
}
/** Solid transparent fill (no hatch) — Colouring Cities style */
function styleForKind(kind) {
    return {
        color: protectionZoneColors[kind],
        fillColor: protectionZoneColors[kind],
        fill: true,
        weight: kind === 'consultationNational' ? 2 : 1.75,
        opacity: 1,
        fillOpacity: fillOpacityForProtectionZone(kind),
        className: `pz-path pz-${kind}`,
    };
}
function ProtectionZoneKindLayer({ kind, data, }) {
    if (!data.features.length)
        return null;
    const style = styleForKind(kind);
    return (React.createElement(GeoJSON, { key: `pz-${kind}-${data.features.length}`, data: data, style: () => style }));
}
export function ConservationAreaBoundaryLayer({}) {
    const [boundaryGeojson, setBoundaryGeojson] = useState(null);
    const { conservation } = useDisplayPreferences();
    useEffect(() => {
        apiGet(getGeometryLayerRequestPath('conservation'))
            .then((data) => {
            if (data && data.type === 'FeatureCollection') {
                setBoundaryGeojson(sortProtectionZones(data));
            }
            else {
                setBoundaryGeojson(null);
            }
        })
            .catch(() => setBoundaryGeojson(null));
    }, []);
    const byKind = useMemo(() => (boundaryGeojson ? partitionByKind(boundaryGeojson) : null), [boundaryGeojson]);
    if (conservation !== 'enabled' || !byKind) {
        return conservation === 'disabled' ? React.createElement("div", null) : React.createElement(React.Fragment, null);
    }
    return (React.createElement(Pane, { name: "cc-protection-zones", style: { zIndex: 350 } }, KIND_ORDER.map((kind) => (React.createElement(ProtectionZoneKindLayer, { key: kind, kind: kind, data: byKind[kind] })))));
}
